import type { SegmentVisualizationData } from 'slices/aiConversations/segmentVisualizationTypes'
import type { Message } from 'slices/aiConversations/types'

export interface SegmentStateAnalysis {
  hasAnySavedSegment: boolean
  latestDraftSegmentMessageId: string | null
  latestSavedSegmentMessageId: string | null
}

/**
 * Analyzes all messages in a conversation to determine segment states.
 * This function performs a single pass through messages to:
 * 1. Check if any segment has been saved (requires_approval or visible state)
 * 2. Find the most recent draft segment
 * 3. Find the most recent saved segment
 *
 * @param messages - Array of messages to analyze
 * @returns Analysis containing saved segment status and latest draft message ID
 */
export const analyzeSegmentStates = (
  messages: Message[]
): SegmentStateAnalysis => {
  let hasAnySavedSegment = false
  let latestDraftSegmentMessageId: string | null = null
  let latestSavedSegmentMessageId: string | null = null

  // Single reverse iteration for efficiency
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i]

    // Collect all visualizations from both singular and array properties
    const visualizations: SegmentVisualizationData[] = []
    if (message.segmentVisualization) {
      visualizations.push(message.segmentVisualization)
    }
    if (message.segmentVisualizations) {
      visualizations.push(...message.segmentVisualizations)
    }

    // Check each visualization in this message
    for (const viz of visualizations) {
      const state = viz.segmentState

      // Check for saved segments
      if (state === 'requires_approval' || state === 'visible') {
        hasAnySavedSegment = true

        // Find latest saved (only set once, on first saved found from end)
        if (!latestSavedSegmentMessageId) {
          latestSavedSegmentMessageId = message.id
        }
      }

      // Find latest draft (only set once, on first draft found from end)
      if (state === 'draft' && !latestDraftSegmentMessageId) {
        latestDraftSegmentMessageId = message.id
      }
    }
  }

  return {
    hasAnySavedSegment,
    latestDraftSegmentMessageId,
    latestSavedSegmentMessageId,
  }
}

/**
 * Checks if any segment in the conversation has been saved.
 * A segment is considered saved if it has 'requires_approval' or 'visible' state.
 * Can be used by parent components to determine if input should be blocked.
 *
 * @param messages - Array of messages to check
 * @returns True if any segment has been saved
 */
export const hasAnySavedSegmentInMessages = (messages: Message[]): boolean => {
  return analyzeSegmentStates(messages).hasAnySavedSegment
}
